import { useFormatter } from '@api/useFormatter'
import { useMetadata } from '@api/useMetadata'
import { useOrderMovements } from '@api/useOrderMovements'
import { ErrorNotification } from '@gravitate-js/excalibrr'
import { useModelConfig } from '@hooks/useModelConfig'
import api from '@utils/api'
import { sortNumeric } from '@utils/general'
import { Col, Form, Row } from 'antd'
import { useEffect, useMemo, useState } from 'react'

import { createDropRequest, handleDropValidation } from '../../../Utils/general'
import { MovementHeader } from '../MovementHeader'
import { DropRow } from './DropRow'
import { generateDropColumnHeaders } from './dropColumnConfig'

export function DropForm({
  order,
  detail,
  setShowingTicketModal,
  setDeliveryTicketData,
  onDropSuccess,
  hasBeenSaved,
  editingDrops,
  setEditing,
  editingBols,
  handleOpenDropAttachmentsModal,
  timezone,
}) {
  const [popConfirmTitle, setPopConfirmTitle] = useState('')
  const { data: bolMetadata } = useMetadata('bol')
  const { saveDropMutation } = useOrderMovements()
  const { mutateAsync } = saveDropMutation()
  const [saveLoading, setSaveLoading] = useState(false)
  const dropKey = hasBeenSaved.drops ? 'store_drops' : 'prefill_store_drops'
  const [form] = Form.useForm()
  const { config } = useModelConfig()
  const backOfficeErp = useMemo(
    () => config?.backoffice_erp_integration?.validation_enabled && config?.backoffice_erp_integration?.file_enabled,
    [config]
  )
  const { formatVolume } = useFormatter()
  const formattedUnit = formatVolume(null, true)?.toUpperCase()
  const showWaterReading = detail?.show_water_reading

  useEffect(() => {
    setEditing(!hasBeenSaved.drops)
    form.setFieldsValue({ ...detail })
  }, [hasBeenSaved.drops])

  const onCancel = () => {
    setEditing(false)
    form.setFieldsValue({ ...form.getFieldsValue(), [dropKey]: detail[dropKey]?.filter((drop) => drop) })
  }

  const saveRequest = async (data) => {
    let formData = data
    setSaveLoading(true)
    if (!formData) formData = form.getFieldsValue()

    const newDrops = createDropRequest(
      { ...detail, [dropKey]: formData[dropKey], order_id: order.order_id },
      dropKey,
      timezone
    )
    const request = { shouldRefresh: hasBeenSaved.bols && !editingBols, drops: newDrops }
    try {
      const data = await mutateAsync(request)
      onDropSuccess(data.orders?.[0])
    } catch (error) {
      ErrorNotification({
        errorFields: [
          {
            errors: ['Failed to save.'],
          },
        ],
      })
    } finally {
      setEditing(false)
      setSaveLoading(false)
    }
  }
  const checkStatus = async (formData) => {
    if (!backOfficeErp) {
      checkTankProductMismatch(formData)
    } else {
      try {
        const hasBeenExported = await api.checkExportStatus({ order_id: order.order_id })
        if (hasBeenExported.data === true) {
          setPopConfirmTitle('This order has already been exported. Are you sure you want to continue?')
        } else {
          checkTankProductMismatch(formData)
        }
      } catch (error) {
        ErrorNotification({
          errorFields: [
            {
              errors: [`Failed to fetch export status.`],
            },
          ],
        })
      }
    }
  }

  const checkTankProductMismatch = (data) => {
    let formData = data?.prefill_store_drops || data?.store_drops
    if (!formData) formData = form.getFieldsValue()
    const mismatches = formData
      .map((drop) => {
        const { store, tank_id, product } = drop
        const storeTanks = (bolMetadata?.stores || []).find((s) => s.store_number === store)?.tanks
        const tankProduct = (storeTanks || []).find((t) => t.tank_id === tank_id)?.product
        if (tankProduct !== product) {
          return `Drop product (${product}) does not match Tank ${tank_id} product (${tankProduct}).`
        }
        return null
      })
      .filter((x) => !!x)

    if (mismatches.length) {
      return setPopConfirmTitle(mismatches.join('  ').concat('  Are you sure you want to continue?'))
    }
    return saveRequest(data)
  }
  const useInches = detail?.unit === 'in'

  return (
    <Form
      form={form}
      size='small'
      onFinish={(formData) => {
        if (!handleDropValidation(formData[dropKey])) {
          return
        }
        return checkStatus(formData)
      }}
      onFinishFailed={ErrorNotification}
    >
      <Form.List name={dropKey}>
        {(fields, { add, remove }) => {
          return (
            <>
              <MovementHeader
                order={order}
                title='SITE DROPS'
                editing={editingDrops}
                setEditing={setEditing}
                addItem={() =>
                  add({
                    store: null,
                    product: null,
                    tank_id: null,
                    date: null,
                    time: null,
                    start_inches: null,
                    start_volume: null,
                    water_inches: null,
                    end_inches: null,
                    end_volume: null,
                    quantity: null,
                  })
                }
                allowCancel={hasBeenSaved.drops}
                onCancel={onCancel}
                setShowingTicketModal={setShowingTicketModal}
                setDeliveryTicketData={setDeliveryTicketData}
                detail={detail}
                orderNumber={order.number}
                saveLoading={saveLoading}
                popConfirmTitle={popConfirmTitle}
                setPopConfirmTitle={setPopConfirmTitle}
                saveRequest={saveRequest}
                form={form}
                handleOpenDropAttachmentsModal={handleOpenDropAttachmentsModal}
              />
              <DropHeaderRow showWaterReading={showWaterReading} formattedUnit={formattedUnit} />
              {fields.map((field, index) => (
                <DropRow
                  key={index}
                  index={index}
                  deleteRow={() => remove(index)}
                  drop={detail[dropKey][index]}
                  field={field}
                  fields={fields}
                  name={dropKey}
                  editing={editingDrops}
                  stores={bolMetadata && sortNumeric(bolMetadata.stores, 'store_number')}
                  form={form}
                  bolMetadata={bolMetadata}
                  showWaterReading={showWaterReading}
                  formattedUnit={formattedUnit}
                  useInches={useInches}
                  timezone={timezone}
                />
              ))}
            </>
          )
        }}
      </Form.List>
    </Form>
  )
}

function DropHeaderRow({ showWaterReading, formattedUnit }) {
  const columnConfig = generateDropColumnHeaders(showWaterReading, formattedUnit)
  return (
    <Row className='p-3 detail-header' data-cy='detail-header'>
      {columnConfig.map((c, i) => {
        return (
          <Col key={i} span={c.span} data-cy={`index:${i}`}>
            {c.value}
          </Col>
        )
      })}
    </Row>
  )
}
